import { Button } from '#/components/Button/Button';
import { RichText } from '#/components/Text/RichText';
import type { CmsApiThreeStepCtaBlockData } from '#/modules/cms/api/types/blocks/CmsApiThreeStepCtaBlock';
import { cmsThreeStepCtaBlockSteps } from '#/modules/cms/api/types/blocks/CmsApiThreeStepCtaBlock';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';

import {
  CmsThreeStepCtaBlock_Box_1,
  CmsThreeStepCtaBlock_Box_2,
  CmsThreeStepCtaBlockHeadlineContainer,
  CmsThreeStepCtaBlockHeadlineRichText,
} from './CmsThreeStepCtaBlock.styled';
import { CmsTreeStepCtaBlockStep } from './CmsTreeStepCtaBlockStep';

type Props = {
  data: CmsApiThreeStepCtaBlockData;
  blockIndex: number;
  pageUpdated: string;
};

export function CmsThreeStepCtaBlock({ data, blockIndex, pageUpdated }: Props) {
  const { mainHeadline, mainText, buttonUrl, buttonText, imageSize, overlap } =
    data;

  const withImages = cmsThreeStepCtaBlockSteps.some(
    (step) => data[`step${step}Image`],
  );

  const withLinks = cmsThreeStepCtaBlockSteps.some(
    (step) => data[`step${step}Url`],
  );

  return (
    <CmsThreeStepCtaBlock_Box_2
      data-qa-id={`three-step-cta-${blockIndex}`}
      data-overlap={overlap ? true : undefined}
    >
      {(mainHeadline ?? mainText) && (
        <CmsThreeStepCtaBlockHeadlineContainer>
          {mainHeadline && (
            <CmsThreeStepCtaBlockHeadlineRichText $noMarginTop>
              {mainHeadline}
            </CmsThreeStepCtaBlockHeadlineRichText>
          )}

          {mainText && (
            <RichText
              html={mainText}
              analyticsData={{
                eventName: 'CMS Block',
                eventProperties: {
                  block_position: blockIndex,
                  block_type: 'Three Step CTA',
                },
              }}
            />
          )}
        </CmsThreeStepCtaBlockHeadlineContainer>
      )}

      <CmsThreeStepCtaBlock_Box_1>
        {cmsThreeStepCtaBlockSteps.map((step, index) => (
          <CmsTreeStepCtaBlockStep
            key={step}
            blockIndex={blockIndex}
            qaId={`step-${index}`}
            headline={data[`step${step}Headline`]}
            text={data[`step${step}Text`]}
            image={data[`step${step}Image`]}
            url={data[`step${step}Url`]}
            withImages={withImages}
            withLinks={withLinks}
            large={imageSize === 'LARGE'}
            pageUpdated={pageUpdated}
            step={step}
          />
        ))}
      </CmsThreeStepCtaBlock_Box_1>

      {buttonText && buttonUrl && (
        <div style={{ marginTop: 24 }}>
          <Button
            variant="primary"
            type="link"
            href={buttonUrl}
            onClick={(event) =>
              trackAnchorClicked(event, 'CMS Block', {
                block_position: blockIndex,
                block_type: 'Three Step Call to Action',
                cta_type: 'Button',
                page_updated: pageUpdated,
              })
            }
          >
            {buttonText}
          </Button>
        </div>
      )}
    </CmsThreeStepCtaBlock_Box_2>
  );
}
